import { useCallback, useRef } from 'react';
import axios from 'axios';
import { useAlbum } from '../contexts/AlbumContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const useApi = () => {
  const {
    projectId,
    setProjectId,
    setUploadedFiles,
    setGroups,
    setLoading,
    setVideoStatus,
    showMessage,
    nextStep,
    updateGroup: updateGroupState,
  } = useAlbum();

  const pollIntervalRef = useRef(null);

  // Step 1: 사진 업로드
  const uploadPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setLoading(true);
    const formData = new FormData();

    for (let file of files) {
      formData.append('files', file);
    }

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadedFiles(response.data.files);
      setProjectId(response.data.project_id);
      showMessage(`${response.data.uploaded_count}개 사진 업로드 완료!`);
      nextStep();
    } catch (error) {
      showMessage(
        `업로드 실패: ${error.response?.data?.detail || error.message}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [setLoading, setUploadedFiles, setProjectId, showMessage, nextStep]);

  // Step 2: 자동 처리 (EXIF + 그룹화 + 위치 식별)
  const processPhotos = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/process/${projectId}`);

      setGroups(response.data.groups);
      showMessage(
        `${response.data.total_photos}장 사진이 ${response.data.groups_count}개 그룹으로 정리되었습니다!`
      );
      nextStep();
    } catch (error) {
      showMessage(
        `처리 실패: ${error.response?.data?.detail || error.message}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, setLoading, setGroups, showMessage, nextStep]);

  // Step 3: 그룹 수정
  const updateGroup = useCallback(async (groupId, field, value) => {
    try {
      await axios.put(`${API_URL}/groups/${projectId}`, {
        group_id: groupId,
        [field]: value,
      });

      // 로컬 상태 업데이트
      updateGroupState(groupId, field, value);
    } catch (error) {
      showMessage(`수정 실패: ${error.message}`, 'error');
    }
  }, [projectId, updateGroupState, showMessage]);

  // 동영상 상태 폴링
  const pollVideoStatus = useCallback(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/video/status/${projectId}`);
        if (response.data.status === 'completed') {
          setVideoStatus('completed');
          showMessage('동영상 생성 완료! 다운로드할 수 있습니다.');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return true;
        }
        return false;
      } catch (error) {
        return false;
      }
    };

    // 5초마다 상태 확인
    pollIntervalRef.current = setInterval(checkStatus, 5000);
  }, [projectId, setVideoStatus, showMessage]);

  // Step 4: 동영상 생성
  const generateVideo = useCallback(async () => {
    setLoading(true);

    try {
      await axios.post(`${API_URL}/video/${projectId}`);
      setVideoStatus('processing');
      showMessage('동영상 생성을 시작했습니다. 완료까지 시간이 걸릴 수 있습니다.', 'info');
      pollVideoStatus();
    } catch (error) {
      showMessage(
        `동영상 생성 실패: ${error.response?.data?.detail || error.message}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, setLoading, setVideoStatus, showMessage, pollVideoStatus]);

  // 다운로드
  const downloadVideo = useCallback(() => {
    window.open(`${API_URL}/download/${projectId}`, '_blank');
  }, [projectId]);

  return {
    uploadPhotos,
    processPhotos,
    updateGroup,
    generateVideo,
    downloadVideo,
  };
};

export default useApi;
